import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Trash2, AlertTriangle, Eye, Loader2, CheckCircle } from 'lucide-react';
import { operatorAPI } from '../../lib/api';
import { toast } from 'sonner';

export default function TestDataCleanup() {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [result, setResult] = useState(null);

  const loadPreview = async () => {
    setLoading(true);
    setPreview(null);
    setResult(null);
    try {
      const response = await operatorAPI.previewTestDataCleanup();
      if (response.data?.ok) {
        setPreview(response.data);
      } else {
        toast.error(response.data?.error || 'Failed to load preview');
      }
    } catch (err) {
      toast.error('Failed to load preview: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (confirmText !== 'DELETE') {
      toast.error('Type DELETE to confirm');
      return;
    }

    setDeleting(true);
    try {
      const response = await operatorAPI.cleanupTestData('DELETE');
      if (response.data?.ok) {
        setResult(response.data);
        setPreview(null);
        setConfirmText('');
        toast.success('Test data cleaned up successfully');
      } else {
        toast.error(response.data?.error || 'Failed to delete');
      }
    } catch (err) {
      toast.error('Failed to delete: ' + err.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="test-data-cleanup-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Test Data Cleanup</h1>
          <p className="text-slate-600 text-sm">Remove test and demo families from production</p>
        </div>
        <Button 
          onClick={loadPreview} 
          disabled={loading}
          variant="outline"
          data-testid="preview-cleanup-btn"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Eye className="h-4 w-4 mr-2" />
          )}
          Preview Test Data
        </Button>
      </div>

      {/* Preview Results */}
      {preview && (
        <Card className="border-amber-200 bg-amber-50" data-testid="cleanup-preview">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-amber-800">
              <AlertTriangle className="h-5 w-5" />
              Data to be Deleted
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Counts */}
            <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
              {Object.entries(preview.counts).map(([key, value]) => (
                <div key={key} className="bg-white rounded-lg p-3 text-center border border-amber-200">
                  <p className="text-2xl font-bold text-amber-700">{value}</p>
                  <p className="text-xs text-slate-600 capitalize">{key.replace(/_/g, ' ')}</p>
                </div>
              ))}
            </div>

            {/* Families List */}
            {preview.test_families?.length > 0 && (
              <div>
                <h4 className="font-medium text-slate-700 mb-2">Test Families ({preview.test_families.length}):</h4>
                <div className="max-h-48 overflow-y-auto bg-white rounded-lg border border-amber-200 p-2">
                  {preview.test_families.map((family) => (
                    <div key={family.id} className="flex items-center justify-between py-2 px-3 hover:bg-slate-50 rounded">
                      <div>
                        <p className="font-medium text-slate-900">{family.family_name}</p>
                        <p className="text-xs text-slate-500">{family.email || 'No email'}</p>
                      </div>
                      {family.is_test_flag && (
                        <Badge variant="outline" className="text-amber-600 border-amber-300">
                          is_test=true
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Confirmation */}
            <div className="border-t border-amber-200 pt-4">
              <p className="text-sm text-amber-800 mb-3">
                <strong>Warning:</strong> This action cannot be undone. Type <code className="bg-amber-100 px-1 rounded">DELETE</code> to confirm.
              </p>
              <div className="flex gap-3">
                <Input
                  placeholder="Type DELETE to confirm"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  className="max-w-xs"
                  data-testid="delete-confirm-input"
                />
                <Button
                  onClick={handleDelete}
                  disabled={confirmText !== 'DELETE' || deleting}
                  variant="destructive"
                  data-testid="delete-test-data-btn"
                >
                  {deleting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4 mr-2" />
                  )}
                  Delete All Test Data
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Result */}
      {result && (
        <Card className="border-green-200 bg-green-50" data-testid="cleanup-result">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-green-800">
              <CheckCircle className="h-5 w-5" />
              Cleanup Complete
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-green-700">{result.message}</p>
            
            {/* Deleted Counts */}
            <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
              {Object.entries(result.deleted_counts || {}).map(([key, value]) => (
                <div key={key} className="bg-white rounded-lg p-3 text-center border border-green-200">
                  <p className="text-2xl font-bold text-green-700">
                    {typeof value === 'number' ? value : '⚠️'}
                  </p>
                  <p className="text-xs text-slate-600 capitalize">{key.replace(/_/g, ' ')}</p>
                </div>
              ))}
            </div>

            {/* Deleted Families */}
            {result.deleted_families?.length > 0 && (
              <div>
                <h4 className="font-medium text-slate-700 mb-2">Deleted Families:</h4>
                <div className="flex flex-wrap gap-2">
                  {result.deleted_families.map((name, i) => (
                    <Badge key={i} variant="outline" className="text-green-700 border-green-300">
                      {name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">How Test Families are Identified</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-600 space-y-2">
          <p>Families are considered "test" if any of the following conditions are true:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><code>is_test = TRUE</code> flag in database</li>
            <li>Family name contains "test" or "demo"</li>
            <li>Parent email contains "+test" or "test@"</li>
            <li>Notes/metadata contain "test", "demo", or "seed"</li>
          </ul>
          <p className="text-amber-600 mt-3">
            <strong>Exception:</strong> Families with "Aruto" in the name are always protected.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
